import { Component } from '@theme/component';

/**
 * A custom element that manages a search dropdown.
 *
 * @typedef {object} Refs
 * @property {HTMLDivElement} dropdownContent - The dropdown content container.
 * @property {HTMLDivElement} backdrop - The backdrop overlay.
 *
 * @extends Component<Refs>
 */
export class SearchDropdownComponent extends Component {
  requiredRefs = ['dropdownContent'];
  optionalRefs = ['backdrop'];

  connectedCallback() {
    super.connectedCallback();
    
    // Close dropdown on escape key
    document.addEventListener('keydown', this.#handleEscapeKey);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#handleClickOutside);
    document.removeEventListener('keydown', this.#handleEscapeKey);
  }

  /**
   * Toggles the dropdown visibility
   */
  toggleDropdown = () => {
    const { dropdownContent } = this.refs;
    const isOpen = !dropdownContent.hasAttribute('hidden');

    if (!isOpen) {
      this.openDropdown();
    } else {
      this.closeDropdown();
    }
  };

  /**
   * Opens the dropdown
   */
  openDropdown = () => {
    const { dropdownContent, backdrop } = this.refs;
    
    dropdownContent.removeAttribute('hidden');
    if (backdrop) {
      backdrop.removeAttribute('hidden');
    }
    
    // Wait until the next tick to add the click listener to avoid race condition
    // when opening is triggered by a click event
    setTimeout(() => {
      document.addEventListener('click', this.#handleClickOutside);
    });
    
    // Focus the search input
    setTimeout(() => {
      const searchInput = dropdownContent.querySelector('input[type="search"]');
      if (searchInput) {
        searchInput.focus();
      }
    }, 100);
  };

  /**
   * Closes the dropdown
   */
  closeDropdown = () => {
    const { dropdownContent, backdrop } = this.refs;
    
    document.removeEventListener('click', this.#handleClickOutside);
    dropdownContent.setAttribute('hidden', '');
    if (backdrop) {
      backdrop.setAttribute('hidden', '');
    }
  };

  /**
   * Closes the dropdown and resets the search
   */
  closeAndResetDropdown = () => {
    // Find the predictive search component and reset the input directly
    const predictiveSearch = this.querySelector('predictive-search-component');
    if (predictiveSearch) {
      const searchInput = predictiveSearch.querySelector('input[type="search"]');
      if (searchInput) {
        searchInput.value = '';
        // Trigger the reset button to hide if it's showing
        const resetButton = predictiveSearch.querySelector('.predictive-search__reset-button');
        if (resetButton) {
          resetButton.hidden = true;
        }
      }
    }
    
    this.closeDropdown();
  };

  /**
   * Handles clicks outside the dropdown
   */
  #handleClickOutside = (event) => {
    const { dropdownContent } = this.refs;
    
    if (dropdownContent.hasAttribute('hidden')) return;
    
    // Check if click is outside the dropdown content and not on the search button
    const searchButton = document.querySelector('.search-action button');
    const clickedInsideDropdown = dropdownContent.contains(event.target);
    const clickedOnSearchButton = searchButton?.contains(event.target);
    
    if (!clickedInsideDropdown && !clickedOnSearchButton) {
      this.closeDropdown();
    }
  };

  /**
   * Handles escape key press
   */
  #handleEscapeKey = (event) => {
    const { dropdownContent } = this.refs;
    
    if (event.key === 'Escape' && !dropdownContent.hasAttribute('hidden')) {
      event.preventDefault();
      this.closeDropdown();
    }
  };

  /**
   * Handles clicks within the dropdown content
   */
  handleDropdownClick = (event) => {
    // Prevent closing dropdown when clicking inside
    event.stopPropagation();
  };
}

if (!customElements.get('search-dropdown-component')) {
  customElements.define('search-dropdown-component', SearchDropdownComponent);
}
